import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  AddPhotoAlternate,
  FileUpload,
  Delete,
  Circle,
  Check,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import readXlsxFile from 'read-excel-file';
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { ApiService } from "./ApiService";
import icon_excel from '../images/icon-excel.png'

const steps = ["Insertar imágenes", "Importar Excel", "Resultado"];

export const ModalImportExcel = ({ isOpen, onClose, current_branch, reload_product }) => {
  /*───────────────────────────*/
  const [activeStep, setActiveStep] = useState(0);
  const [images, setImages] = useState([]); // { file, preview, status, url }
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const [data_excel, setData_excel] = useState([]);
  const [data_file, setData_file] = useState({});
  const file_excelRef = useRef(null);

  const [result_imported, setResult_imported] = useState({});
  const message_fail = "Sin datos";
  const obj_messages = {
    failed: "Fallido",
    repeated: "Repetido",
    success: "Exitoso",
  };


  const squareBox = {
    position: "relative",
    width: "100%",
    paddingTop: "100%",
    borderRadius: 2,
    overflow: "hidden",
  };

  const allUploaded = images.length > 0 && images.every((i) => i.status === "success");

  /*───────────────────────────*/
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = /\.(png|jpe?g)$/i;
    const newImgs = [];



    files.forEach((file) => {
      if (!allowed.test(file.name)) {
        toast.error("Solo se permiten imágenes .png, .jpg o .jpeg");
      } else {
        newImgs.push({ file, preview: URL.createObjectURL(file), status: "idle", url: null });
      }
    });

    if (newImgs.length) {
      setImages((prev) => [...prev.map((i) => ({ ...i, status: "idle", url: null })), ...newImgs]);
    }
    e.target.value = "";
  };

  const handleRemove = (idx) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      const rest = prev.filter((_, i) => i !== idx);
      return rest.map((i) => ({ ...i, status: "idle", url: null }));
    });
  };

  /*───────────────────────────*/
  const uploadImages = useCallback(async () => {
    if (!images.length) {
      toast.warning("Selecciona al menos una imagen");
      return;
    }

    setUploading(true);

    const updated = images.map(array_img => ({
      ...array_img,
      status: array_img.status != "success" ? "uploading" : array_img.status
    }));

    setImages(updated);


    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === "success") continue;

      const formData = new FormData();
      formData.append("file", updated[i].file);

      try {
        const request_img = await ApiService.fetchData(
          `api/company/upload/image?key_branch=${current_branch}`,
          {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          },
          false
        );

        const response = request_img || {};
        updated[i].status = response?.code == 201 || response?.code == 409 ? "success" : "error";
      } catch (err) {
        // console.error("Error al subir imagen:", err);
        updated[i].status = "error";
      }

      setImages([...updated]);
    }

    setUploading(false);
    if (updated.every((i) => i.status === "success")) console.info("URLs:", updated.map((i) => i.url));
  }, [images, current_branch]);

  /*───────────────────────────*/
  const renderImagesGrid = () => (
    <Grid container spacing={2}>
      {images.map((img, idx) => (
        <Grid item xs={4} sm={3} md={2} key={idx}>
          <Box sx={{ ...squareBox, boxShadow: 1 }}>
            <img src={img.preview} alt="preview" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: img.status === "uploading" ? "grayscale(70%)" : "none" }} />

            {img.status === "uploading" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.45)" }}>
                <CircularProgress size={48} />
              </Box>
            )}

            {img.status === "success" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.4)" }}>
                <Circle sx={{ fontSize: 100, color: "success.main" }} />
                <Check sx={{ position: "absolute", fontSize: 75, color: "#fff" }} />
              </Box>
            )}

            {img.status === "error" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.4)" }}>
                <Circle sx={{ fontSize: 100, color: "error.main" }} />
                <CancelIcon sx={{ position: "absolute", fontSize: 70, color: "#fff" }} />
              </Box>
            )}

            <IconButton size="small" onClick={() => handleRemove(idx)} sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(216,10,10,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(195,16,16,0.8)" } }}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      ))}

      <Grid item xs={4} sm={3} md={2}>
        <Box sx={{ ...squareBox, border: "2px dashed", borderColor: "divider", cursor: "pointer", "&:hover": { borderColor: "primary.main", color: "primary.main" } }} onClick={() => inputRef.current?.click()}>
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
            <AddPhotoAlternate fontSize="large" />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );

  const handleExcel = (event) => {
    const file = event.target.files[0];

    let file_size = file.size ?? 0;

    if (file_size < 1024) {
      file_size = `${file_size} Bytes`;
    } else if (file_size < 1024 * 1024) {
      let kb = file_size / 1024;
      file_size = `${kb.toFixed(2)} KB`;
    } else {
      let mb = file_size / (1024 * 1024);
      file_size = `${mb.toFixed(2)} MB`;
    }

    data_file.name = file?.name;
    data_file.size = file_size;

    const file_info = {
      'name': file.name ?? 'Archivo Invalido...',
      'size': file_size
    };

    setData_file(file_info);

    if (file) {
      readXlsxFile(file).then((rows) => {

        const newData = rows.filter((datos, index) => {
          return index != 0;
        }).map((result, position) => {
          const new_result = result.slice(0, 14);

          if (new_result.length != 14) return [null, null, null, null, null, null, null, null, null, null, null, null, null, null, null];

          const detalle_separacion = new_result[12].split("%%");

          const obj_detalle = {};

          if (detalle_separacion.length > 0) {
            detalle_separacion.forEach(data_split => {
              const campo_valor = data_split.split(":");

              if (campo_valor.length == 2) obj_detalle[campo_valor[0]] = campo_valor[1].trim();
            });
          }

          new_result[12] = obj_detalle;
          new_result[13] = (new_result[13].split(",")).map((img_url) => {
            let new_url = img_url.split("\\");
            return new_url[new_url.length - 1];
          });

          return new_result;
        });
        setData_excel(newData);
      });
    }
  }

  const upload_data = async () => {
    setUploading(true);

    const data_formated = data_excel.map((values) => {
      return {
        code: values[0],
        product_name: values[1],
        city: values[2],
        purchase_price: conver_price(values[3]),
        price1: conver_price(values[4]),
        price2: conver_price(values[5]),
        price3: conver_price(values[6]),
        wholesale_price: conver_price(values[7]),
        stock: values[9],
        category: values[8],
        supplier: values[10],
        brand: values[11],
        detail: values[12],
        img_list: values[13],
      };
    });

    const fetch_import = await ApiService.fetchData(`api/company/product/import?key_branch=${current_branch}`, {
      method: "POST",
      body: JSON.stringify({ data_import: data_formated }),
    })

    if (fetch_import.result !== undefined) setResult_imported(fetch_import);
    else setResult_imported({ result: "failed", result_imported: [] });

    setUploading(false);
    setActiveStep(2);

    return;
  }

  const conver_price = (price) => {
    return parseFloat(price).toFixed(2);
  }

  function import_ended() {
    if (result_imported?.result == "success") {
      setActiveStep(0);
      setImages([]);
      setUploading(false);
      // inputRef = null;
      setData_excel([]);
      setData_file({});
      // file_excelRef = null;
      setResult_imported({});
    } else {
      setActiveStep(1);
    }

    if(result_imported?.count_success>0) reload_product();

    onClose();
  }

  const return_value = (posicion = 0, list_code = "") => {
    const code_converted = posicion + "_" + list_code;
    const find_value = result_imported.detail_imported[code_converted] ?? "Sin valor";

    return obj_messages[find_value] ?? find_value;
  }
  /*───────────────────────────*/
  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth={false} PaperProps={{ sx: { width: "90vw", height: "90vh", position: "relative", paddingTop: '40px', paddingRight: '10px' } }}>
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pb: 10, overflowY: 'auto', maxHeight: 'calc(90vh - 150px)' }}>
        {activeStep === 0 && (
          <>
            {renderImagesGrid()}
            <input type="file" hidden multiple ref={inputRef} accept="image/png,image/jpg,image/jpeg" onChange={handleFileSelect} />
          </>
        )}
        {activeStep === 1 && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            {
              data_file.name ? (
                <Box sx={{ width: 'fit-content', display: 'flex', flexFlow: 'row nowrap', gap: '15px', padding: '12px', borderRadius: '20px', backgroundColor: '#f4f8ff', fontSize: 'clamp(0.7rem, 2vw, 1rem)', margin: 'auto' }}>
                  <img src={icon_excel} style={{ width: '50px', objectFit: 'contain' }} />
                  <div style={{ width: '100%', display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', textAlign: 'start' }}>
                    <span>{data_file.name}</span>
                    <span>{data_file.size}</span>
                  </div>

                  <div style={{ height: 'inherit', margin: 'auto' }}>
                    <button style={{ border: 'none', backgroundColor: 'transparent' }} onClick={() => {
                      setData_excel([]);
                      setData_file({});
                      file_excelRef.current.value = null;
                    }}>
                      <Delete />
                    </button>
                  </div>
                </Box>
              ) : (
                <Box sx={{ border: "3px dashed", borderColor: "divider", cursor: "pointer", "&:hover": { borderColor: "primary.main", color: "primary.main" } }} onClick={() => file_excelRef.current?.click()}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexFlow: 'column nowrap', color: "text.secondary" }}>
                    <span>Selecciona un Excel</span>
                    <FileUpload fontSize="large" />
                  </Box>
                </Box>
              )
            }

            <input type="file" hidden ref={file_excelRef} accept=".xlsx" onChange={handleExcel} />
            {/* <input type="file" hidden multiple ref={file_excelRef} accept=".xls,.xlsx,application/vnd.ms-excel" onChange={handleExcel} /> */}
            <br></br>
            {
              data_excel && data_excel.length > 0 && (
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Code</th>
                      <th scope="col">Nombre</th>
                      <th scope="col">Ciudad</th>
                      <th scope="col">P. Compra</th>
                      <th scope="col">Precio#1</th>
                      <th scope="col">Precio#2</th>
                      <th scope="col">Precio#3</th>
                      <th scope="col">P. Mayor</th>
                      <th scope="col">Categoria</th>
                      <th scope="col">Stock</th>
                      <th scope="col">Proveedor</th>
                      <th scope="col">Marca</th>
                    </tr>
                  </thead>
                  <tbody >
                    {data_excel.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          cellIndex < 12 && (<td key={cellIndex}>{typeof cell === 'object' ? 'Este valor es un objeto' : cell}</td>)
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }

          </Box>
        )}
        {activeStep === 2 && (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <h2>Resultado de Importación:</h2>

            <dl className="d-flex flex-row justify-content-around">
              <div className="d-flex flex-column">
                <div className="row">
                  <dt>Datos procesados:&nbsp;</dt>
                  <dd>{data_excel.length}</dd>
                </div>


                <div className="row">
                  <dt>Datos rechazados:&nbsp;</dt>
                  <dd>{result_imported.count_rejected ?? message_fail}</dd>
                </div>

                <div className="row">
                  <dt>Conclusión:&nbsp;</dt>
                  <dd><span style={{ color: result_imported?.result == "success" ? "#28a745" : "#dc3545" }}>{obj_messages[result_imported.result] ?? message_fail}</span> </dd>
                </div>

              </div>

              <div className="d-flex flex-column">
                <div className="row">
                  <dt>Agregados:&nbsp;</dt>
                  <dd>{result_imported.count_success ?? message_fail}</dd>
                </div>

                <div className="row">
                  <dt>Repetidos:&nbsp;</dt>
                  <dd>{result_imported.count_repeted ?? message_fail}</dd>
                </div>
              </div>

              <div className="d-flex flex-column">
                <div className="row">
                  <dt>Fallidos:&nbsp;</dt>
                  <dd>{result_imported.count_failed ?? message_fail}</dd>
                </div>

                <div className="row">
                  <dt>Error de proveedor:&nbsp;</dt>
                  <dd>{result_imported.count_no_found ?? message_fail}</dd>
                </div>
              </div>
            </dl>

            <h3>Detalle de importación:</h3>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Posicion</th>
                  <th scope="col">Codigo</th>
                  <th scope="col">Resultado</th>
                </tr>
              </thead>

              <tbody>
                {
                  data_excel.map((data, index) => (
                    <tr>
                      <td>{index + 1}</td>
                      <td>{typeof data[0] !== "object" ? data[0] : "Sin codigo"}</td>
                      <td>{return_value(index, data[0]) ?? "nada"}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </Box>
        )}
      </DialogContent>

      {/* Inferior */}
      <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", bgcolor: "background.paper", p: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          {/* Botón Cancelar (parte izquierda) */}
          <Button variant="outlined" onClick={onClose}>Cancelar</Button>

          {/* Controles derechos */}
          <Box>
            <Button variant="outlined" onClick={() => setActiveStep((s) => Math.max(0, s - 1))} disabled={activeStep === 0} sx={{ mr: 1 }}>Atrás</Button>

            {
              <Button
                variant="contained"
                onClick={
                  () => {
                    if (activeStep == 0 && allUploaded) setActiveStep(1);
                    else uploadImages();
                    if (activeStep == 1 && data_excel) upload_data();
                    if (activeStep == 2) import_ended();
                  }
                }
                disabled={
                  (uploading || images.length == 0 || (activeStep == 1 && data_excel.length == 0))
                }
              >
                {
                  (allUploaded && activeStep == 0) ?
                    "Siguiente"
                    :
                    (result_imported?.result && activeStep == 2 ? (result_imported?.result == "success" ? "Finalizar" : "Cerrar") :
                      (
                        (uploading ? "Enviando " : "Enviar ") + (activeStep == 0 ? "Imágenes..." : "Datos...")
                      )
                    )
                }
              </Button>
            }
          </Box>
        </Box>
      </Box>
    </Dialog >
  );
};
